<template lang="pug">
  #p2p-converter
    WtLabel(
      isLabelElement
      text="Program Vs Program Challenge Converter"
      tooltip-text="Convert team/group challenges to Program-to-Program (P2P) challenges by adding participating programs."
    )
    .section-title
      WtButton(
        is-router-link
        @click.native="openConverterModal"
        ) Challenge Converter

    BModal(:active.sync="converterModalOpen" :width="800")
      .modal-card(style='width: auto')
        header.modal-card-head.title-header
          p.modal-card-title Program Vs program Challenge Converter
        section.modal-card-body
          .columns
            .column
              BField(label="Select Challenge")
                BSelect(
                  placeholder="Select a challenge"
                  v-model="selectedChallenge"
                  :loading="loadingChallenges || loadingExistingPrograms"
                  expanded
                )
                  option(
                    v-for="challenge in eligibleChallenges"
                    :key="challenge.id"
                    :value="challenge"
                  )
                    | {{ challenge.name }} ({{ challengeType(challenge) }})

          template(v-if="selectedChallenge")
            //- Show existing programs info if challenge already has programs
            .columns(v-if="existingProgramsCount > 0 && !hasModifiedPrograms")
              .column
                BNotification(type="is-info is-light" :closable="false")
                  p
                    BIcon(icon="information" size="is-small")
                    span.ml-2 This challenge already has {{ existingProgramsCount }} program(s) assigned.
                  p.has-text-grey.is-size-7 You can add more programs or remove existing ones below.

            .columns(v-if="selectedPrograms.length > 0")
              .column
                BField
                  template(slot="label")
                    span {{ existingProgramsCount > 0 ? 'All Selected Programs' : 'Selected Programs' }}
                    BTag.programs-count(type="is-info" rounded)
                      | {{ selectedPrograms.length }}
                    span.is-size-7.ml-2.has-text-grey(v-if="existingProgramsCount > 0")
                      | ({{ existingProgramsCount }} existing, {{ newProgramsCount }} new)
                .tags
                  BTag(
                    v-for="program in selectedPrograms"
                    :key="program.id"
                    :closable="program.id !== currentProgramId"
                    :type="isExistingProgram(program) ? 'is-info' : 'is-primary'"
                    @close="removeProgram(program)"
                  )
                    | {{ program.name }}
                    span.is-size-7.ml-1(v-if="isExistingProgram(program)") (existing)
                hr

            //- Program Selection Dropdown
            .columns.program-search-bar
              .column
                BField(label="Search and Select Programs to Add")
                  WtMultiSelectDropDown(
                    :value="selectedPrograms"
                    :options="filteredAvailablePrograms"
                    :async-search="true"
                    :loading="loadingPrograms"
                    placeholder="Type to search programs..."
                    label="name"
                    track-by="id"
                    @selectUnselect="handleProgramSelection"
                    @search="handleSearchPrograms"
                  )

        footer.modal-card-foot.challenge-converter-footer
          WtButton.button.is-danger(@click.native="closeModal") Cancel
          WtButton.button.is-primary(
            @click.native="convertToP2p"
            :disabled="!canConvert"
            :loading="converting"
          ) Convert to P2P

</template>

<script>
import Vue from "vue";
import { mapActions } from "vuex";
import WtLabel from "@@/WtLabel/WtLabel";
import WtButton from "@@/WtButton/WtButton";
import WtMultiSelectDropDown from "@@/WtMultiSelectDropDown/WtMultiSelectDropDown";
import { challengeMixin } from "@/mixins/challengeMixin";

export default {
  name: "P2PConverter",

  components: {
    WtLabel,
    WtButton,
    WtMultiSelectDropDown
  },

  mixins: [challengeMixin],

  data() {
    return {
      converterModalOpen: false,
      selectedChallenge: null,
      selectedPrograms: [],
      existingPrograms: [],
      availablePrograms: [],
      allChallenges: [],
      converting: false,
      loadingChallenges: false,
      loadingPrograms: false,
      loadingExistingPrograms: false,
      searchDebounce: null
    };
  },

  computed: {
    eligibleChallenges() {
      // Filter for group/team challenges or challenges between teams, excluding completed challenges
      return this.allChallenges.filter(challenge => {
        return (
          (challenge.between === "groups" || challenge.isPvsPChallenge) &&
          !challenge.isCompleted
        );
      });
    },
    currentProgramId() {
      return Vue.auth?.user()?.programId || null;
    },
    canConvert() {
      return (
        this.selectedChallenge &&
        this.selectedPrograms.length > 0 &&
        !this.converting
      );
    },

    filteredAvailablePrograms() {
      const selectedIds = new Set(this.selectedPrograms.map(p => p.id));
      return this.availablePrograms.filter(p => !selectedIds.has(p.id));
    },

    existingProgramsCount() {
      return this.existingPrograms.length;
    },

    newProgramsCount() {
      const existingIds = new Set(this.existingPrograms.map(p => p.id));
      return this.selectedPrograms.filter(p => !existingIds.has(p.id)).length;
    },

    hasModifiedPrograms() {
      const existingIds = new Set(this.existingPrograms.map(p => p.id));
      const selectedIds = new Set(this.selectedPrograms.map(p => p.id));

      if (this.existingPrograms.length !== this.selectedPrograms.length) {
        return true;
      }

      for (const id of existingIds) {
        if (!selectedIds.has(id)) return true;
      }

      for (const id of selectedIds) {
        if (!existingIds.has(id)) return true;
      }

      return false;
    }
  },

  watch: {
    converterModalOpen(newVal) {
      if (newVal) {
        this.fetchAllChallenges();
        this.fetchAllPrograms();
      } else {
        // Reset state when modal closes
        this.resetForm();
      }
    },

    selectedChallenge(newVal) {
      if (newVal) {
        this.fetchExistingPrograms(newVal.id);
      } else {
        this.existingPrograms = [];
        this.selectedPrograms = [];
      }
    }
  },

  methods: {
    ...mapActions(["snackBar"]),
    ...mapActions("superAdmin", [
      "convertChallengeToP2p",
      "fetchProgramChallenges",
      "fetchChallengeMembers"
    ]),
    ...mapActions("program", ["searchPrograms"]),

    async fetchAllChallenges() {
      this.loadingChallenges = true;
      try {
        const programId = this.currentProgramId;
        this.allChallenges = await this.fetchProgramChallenges(programId);
      } catch (error) {
        console.log("the error is ", error.message);
        this.snackBar({
          type: "is-danger",
          message: "Failed to fetch challenges. Please try again.",
          duration: 5000
        });
      } finally {
        this.loadingChallenges = false;
      }
    },

    async fetchAllPrograms() {
      this.loadingPrograms = true;
      try {
        const response = await this.searchPrograms("");
        this.availablePrograms = response.data || [];
      } catch (error) {
        this.snackBar({
          type: "is-danger",
          message: "Failed to fetch programs. Please try again.",
          duration: 5000
        });
      } finally {
        this.loadingPrograms = false;
      }
    },

    handleSearchPrograms(query) {
      // Clear previous debounce
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }

      // Debounce search to avoid too many API calls
      this.searchDebounce = setTimeout(async () => {
        this.loadingPrograms = true;
        try {
          const response = await this.searchPrograms(query);
          this.availablePrograms = response.data || [];
        } catch (error) {
          this.snackBar({
            type: "is-danger",
            message: "Failed to search programs. Please try again.",
            duration: 5000
          });
        } finally {
          this.loadingPrograms = false;
        }
      }, 300); // 300ms debounce
    },

    openConverterModal() {
      this.converterModalOpen = true;
    },

    closeModal() {
      this.converterModalOpen = false;
      this.resetForm();
    },

    resetForm() {
      this.selectedChallenge = null;
      this.selectedPrograms = [];
      this.existingPrograms = [];
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }
    },

    async fetchExistingPrograms(challengeId) {
      this.loadingExistingPrograms = true;
      try {
        this.existingPrograms = await this.fetchChallengeMembers(challengeId);
        this.selectedPrograms = [...this.existingPrograms];
      } catch (error) {
        console.error("Failed to fetch existing programs:", error);
        this.snackBar({
          type: "is-warning",
          message:
            "Could not load existing programs for this challenge. You can still add new ones.",
          duration: 5000
        });
        this.existingPrograms = [];
        this.selectedPrograms = [];
      } finally {
        this.loadingExistingPrograms = false;
      }
    },

    isExistingProgram(program) {
      return this.existingPrograms.some(p => p.id === program.id);
    },

    handleProgramSelection(programs) {
      const newSelection = programs || [];
      const uniqueMap = new Map();
      newSelection.forEach(program => {
        uniqueMap.set(program.id, program);
      });
      this.selectedPrograms = Array.from(uniqueMap.values());
    },

    removeProgram(program) {
      this.selectedPrograms = this.selectedPrograms.filter(
        p => p.id !== program.id
      );
    },

    async convertToP2p() {
      if (!this.canConvert) return;

      this.converting = true;

      try {
        let programIds = this.selectedPrograms.map(p => p.id);

        let currentProgramId = this.currentProgramId;

        if (currentProgramId && !programIds.includes(currentProgramId)) {
          programIds.push(this.currentProgramId);
        }

        await this.convertChallengeToP2p({
          challengeId: this.selectedChallenge.id,
          challengeMembers: programIds
        });

        this.snackBar({
          type: "is-success",
          message: `Challenge "${this.selectedChallenge.name}" has been successfully converted to P2P!`,
          duration: 5000
        });

        this.closeModal();
      } catch (error) {
        const errorMessage = this._.get(
          error,
          "response.data.error.message",
          "Failed to convert challenge to P2P. Please try again."
        );
        this.snackBar({
          type: "is-danger",
          message: errorMessage,
          duration: 5000
        });
      } finally {
        this.converting = false;
      }
    }
  }
};
</script>

<style lang="sass" scoped>
#p2p-converter
  .title-header
    padding-left: 250px
  .program-search-bar
    margin-bottom: 25px
  .programs-count
    margin-left: 10px
    background-color: #0288d1
  .challenge-converter-footer
    gap: 10px
  .section-title
    margin-top: 10px
  /deep/ .section
    padding: 0px 0px
</style>
